// Expense validators

import { z } from 'zod';
import {
  dateSchema,
  currencyCodeSchema,
  expenseStatusEnumSchema,
  expenseFrequencyEnumSchema,
} from '../utils/validation';

// Shared expense item schema
const expenseItemSchema = z.object({
  description: z.string().min(1).max(500),
  quantity: z.number().positive(),
  unitPrice: z.number().min(0),
  taxRate: z.number().min(0).max(100).default(0),
  discountAmount: z.number().min(0).optional(),
  discountPercentage: z.number().min(0).max(100).optional(),
  productId: z.string().optional(),
  serviceId: z.string().optional(),
});

// Create expense validator
export const createExpenseSchema = z.object({
  categoryId: z.string().optional(),
  categoryName: z.string().max(100).optional(),
  description: z.string().max(500).optional(),
  items: z.array(expenseItemSchema).min(1),
  currency: currencyCodeSchema.default('IDR'),
  expenseDate: dateSchema,
  paymentMethod: z.string().max(50).optional(),
  reference: z.string().max(200).optional(),
  vendor: z.string().max(200).optional(),
  notes: z.string().max(1000).optional(),
  attachmentUrls: z.array(z.string()).optional(),
  status: z.enum(['draft', 'pending']).default('draft'),
  applyTax: z.boolean().optional(),
  applyPph23: z.boolean().optional(),
  pph23Rate: z.number().min(0).max(100).optional(),
  pph23GrossUp: z.boolean().optional(),
  discount: z.object({ name: z.string(), amount: z.number().min(0) }).optional(),
});

// Update expense validator
export const updateExpenseSchema = z.object({
  categoryId: z.string().optional(),
  categoryName: z.string().max(100).optional(),
  description: z.string().max(500).optional(),
  items: z.array(expenseItemSchema).optional(),
  currency: currencyCodeSchema.optional(),
  expenseDate: dateSchema.optional(),
  paymentMethod: z.string().max(50).optional(),
  reference: z.string().max(200).optional(),
  vendor: z.string().max(200).optional(),
  notes: z.string().max(1000).optional(),
  attachmentUrls: z.array(z.string()).optional(),
  applyTax: z.boolean().optional(),
  applyPph23: z.boolean().optional(),
  pph23Rate: z.number().min(0).max(100).optional(),
  pph23GrossUp: z.boolean().optional(),
  discount: z.object({ name: z.string(), amount: z.number().min(0) }).optional(),
});

// Expense filter validator
export const expenseFilterSchema = z.object({
  categoryId: z.string().optional(),
  status: expenseStatusEnumSchema.optional(),
  dateFrom: dateSchema.optional(),
  dateTo: dateSchema.optional(),
  search: z.string().optional(),
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(1).max(100).optional(),
  sortBy: z.enum(['expenseNumber', 'expenseDate', 'totalAmount', 'createdAt']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});

// Expense category validators
export const createExpenseCategorySchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name cannot exceed 100 characters'),
  description: z.string().max(500).optional(),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Color must be a valid hex color')
    .optional(),
});

export const updateExpenseCategorySchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(500).optional(),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  isActive: z.boolean().optional(),
});

// Recurring expense config validators
export const createRecurringExpenseSchema = z.object({
  name: z.string().min(1, 'Name is required').max(200),
  categoryId: z.string().optional(),
  categoryName: z.string().max(100).optional(),
  items: z.array(expenseItemSchema).min(1),
  currency: currencyCodeSchema.default('IDR'),
  vendor: z.string().max(200).optional(),
  frequency: expenseFrequencyEnumSchema,
  startDate: dateSchema,
  endDate: dateSchema.optional(),
  applyTax: z.boolean().optional(),
  applyPph23: z.boolean().optional(),
  pph23Rate: z.number().min(0).max(100).optional(),
  pph23GrossUp: z.boolean().optional(),
  discount: z.object({ name: z.string(), amount: z.number().min(0) }).optional(),
});

export const updateRecurringExpenseSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  categoryId: z.string().optional(),
  categoryName: z.string().max(100).optional(),
  items: z.array(expenseItemSchema).optional(),
  currency: currencyCodeSchema.optional(),
  vendor: z.string().max(200).optional(),
  frequency: expenseFrequencyEnumSchema.optional(),
  startDate: dateSchema.optional(),
  endDate: dateSchema.optional(),
  isActive: z.boolean().optional(),
  applyTax: z.boolean().optional(),
  applyPph23: z.boolean().optional(),
  pph23Rate: z.number().min(0).max(100).optional(),
  pph23GrossUp: z.boolean().optional(),
  discount: z.object({ name: z.string(), amount: z.number().min(0) }).optional(),
});

// Types
export type CreateExpenseInput = z.infer<typeof createExpenseSchema>;
export type UpdateExpenseInput = z.infer<typeof updateExpenseSchema>;
export type ExpenseFilterInput = z.infer<typeof expenseFilterSchema>;
export type CreateExpenseCategoryInput = z.infer<typeof createExpenseCategorySchema>;
export type UpdateExpenseCategoryInput = z.infer<typeof updateExpenseCategorySchema>;
export type CreateRecurringExpenseInput = z.infer<typeof createRecurringExpenseSchema>;
export type UpdateRecurringExpenseInput = z.infer<typeof updateRecurringExpenseSchema>;
